import { useId, type ReactNode } from 'react';

/**
 * Campos de captura pensados para el telefono.
 *
 * Detalles que importan en iPhone:
 *   - `inputMode` elige el teclado: numerico o decimal (Req. 19.3)
 *   - la fuente de 16 px evita que iOS acerque la pantalla al enfocar
 *   - la altura minima cumple el area tactil de 44 puntos (Req. 19.2)
 *   - el error se anuncia con aria-describedby y no solo con color (Req. 19.7)
 */

const CLASES_ENTRADA =
  'w-full min-h-11 rounded-xl border bg-superficie px-3 py-2.5 text-base text-texto ' +
  'placeholder:text-texto-tenue disabled:bg-superficie-suave disabled:text-texto-tenue';

function clasesCon(error: boolean): string {
  return `${CLASES_ENTRADA} ${error ? 'border-alerta' : 'border-borde-fuerte'}`;
}

interface Envoltura {
  etiqueta: string;
  ayuda?: string;
  error?: string | null;
  children: (props: { id: string; describedBy: string | undefined; hayError: boolean }) => ReactNode;
}

function Campo({ etiqueta, ayuda, error, children }: Envoltura) {
  const id = useId();
  const idAyuda = `${id}-ayuda`;
  const idError = `${id}-error`;
  const hayError = error !== null && error !== undefined && error !== '';

  const describedBy =
    [hayError ? idError : null, ayuda !== undefined ? idAyuda : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-texto">
        {etiqueta}
      </label>
      {children({ id, describedBy, hayError })}
      {ayuda !== undefined && !hayError && (
        <p id={idAyuda} className="text-xs text-texto-suave">
          {ayuda}
        </p>
      )}
      {hayError && (
        <p id={idError} className="text-xs font-medium text-alerta">
          ⚠ {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsTexto {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
  maxLength?: number;
  lista?: readonly string[];
}

export function TextField({
  etiqueta,
  valor,
  onCambio,
  ayuda,
  error,
  placeholder,
  autoFocus,
  maxLength,
  lista,
}: PropsTexto) {
  const idLista = useId();

  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <>
          <input
            id={id}
            type="text"
            value={valor}
            onChange={(e) => onCambio(e.target.value)}
            className={clasesCon(hayError)}
            placeholder={placeholder}
            aria-describedby={describedBy}
            aria-invalid={hayError}
            autoFocus={autoFocus}
            maxLength={maxLength}
            list={lista !== undefined && lista.length > 0 ? idLista : undefined}
            autoComplete="off"
          />
          {lista !== undefined && lista.length > 0 && (
            <datalist id={idLista}>
              {lista.map((opcion) => (
                <option key={opcion} value={opcion} />
              ))}
            </datalist>
          )}
        </>
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------

interface PropsNumero {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
  placeholder?: string;
  autoFocus?: boolean;
  /** true = admite decimales, como 2.49 kg. */
  decimal?: boolean;
}

/**
 * Campo numerico. Se maneja como texto a proposito: convertirlo a numero en
 * cada tecla impediria escribir "2." mientras se teclea "2.49".
 */
export function NumberField({
  etiqueta,
  valor,
  onCambio,
  ayuda,
  error,
  placeholder,
  autoFocus,
  decimal = true,
}: PropsNumero) {
  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <input
          id={id}
          type="text"
          inputMode={decimal ? 'decimal' : 'numeric'}
          value={valor}
          onChange={(e) => {
            // Solo digitos, un punto y coma como separador alterno
            const limpio = e.target.value.replace(decimal ? /[^0-9.,]/g : /[^0-9]/g, '');
            onCambio(limpio.replace(',', '.'));
          }}
          className={`${clasesCon(hayError)} text-right tabular-nums`}
          placeholder={placeholder}
          aria-describedby={describedBy}
          aria-invalid={hayError}
          autoFocus={autoFocus}
          autoComplete="off"
        />
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------

interface PropsDinero {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
  autoFocus?: boolean;
}

/** Campo de importe. El signo de pesos va fuera, para no estorbar al escribir. */
export function MoneyField({ etiqueta, valor, onCambio, ayuda, error, autoFocus }: PropsDinero) {
  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-texto-suave"
          >
            $
          </span>
          <input
            id={id}
            type="text"
            inputMode="decimal"
            value={valor}
            onChange={(e) => onCambio(e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.'))}
            className={`${clasesCon(hayError)} pl-7 text-right tabular-nums`}
            placeholder="0.00"
            aria-describedby={describedBy}
            aria-invalid={hayError}
            autoFocus={autoFocus}
            autoComplete="off"
          />
        </div>
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------

export interface OpcionSelect {
  valor: string;
  etiqueta: string;
}

interface PropsSelect {
  etiqueta: string;
  valor: string;
  opciones: readonly OpcionSelect[];
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
}

export function SelectField({
  etiqueta,
  valor,
  opciones,
  onCambio,
  ayuda,
  error,
}: PropsSelect) {
  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <select
          id={id}
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
          className={clasesCon(hayError)}
          aria-describedby={describedBy}
          aria-invalid={hayError}
        >
          {opciones.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.etiqueta}
            </option>
          ))}
        </select>
      )}
    </Campo>
  );
}

// ---------------------------------------------------------------------------

interface PropsFecha {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  ayuda?: string;
  error?: string | null;
}

/** Fecha en formato AAAA-MM-DD, que es como se guarda en la base. */
export function DateField({ etiqueta, valor, onCambio, ayuda, error }: PropsFecha) {
  return (
    <Campo etiqueta={etiqueta} ayuda={ayuda} error={error}>
      {({ id, describedBy, hayError }) => (
        <input
          id={id}
          type="date"
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
          className={clasesCon(hayError)}
          aria-describedby={describedBy}
          aria-invalid={hayError}
        />
      )}
    </Campo>
  );
}
